import React from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import styles from "./CalendaCalendar.module.css";
import { getProfessionalColor } from "./calendarHelpers";

/**
 * Header do calendário: navegação (anterior / hoje / próximo), seletor de view
 * (Semana / Dia) e legenda de profissionais com a cor determinística.
 */
const CalendarHeader = ({
  view,
  onViewChange,
  rangeLabel,
  onPrev,
  onToday,
  onNext,
  professionals = [],
  professionalColors = {},
}) => {
  const hasProfessionals = professionals.length > 0;

  return (
    <header className={styles.header}>
      <div className={styles.headerTop}>
        <div className={styles.nav}>
          <button
            className={styles.navBtn}
            onClick={onPrev}
            aria-label={view === "day" ? "Dia anterior" : "Semana anterior"}
          >
            &lt;
          </button>
          <button className={styles.navTodayBtn} onClick={onToday}>
            Hoje
          </button>
          <button
            className={styles.navBtn}
            onClick={onNext}
            aria-label={view === "day" ? "Próximo dia" : "Próxima semana"}
          >
            &gt;
          </button>
          {rangeLabel && <span className={styles.rangeLabel}>{rangeLabel}</span>}
        </div>

        <div className={styles.viewSwitch} role="group" aria-label="Selecionar visualização">
          <button
            className={`${styles.viewBtn}${view === "week" ? ` ${styles.viewBtnActive}` : ""}`}
            onClick={() => onViewChange("week")}
            aria-pressed={view === "week"}
          >
            Semana
          </button>
          <button
            className={`${styles.viewBtn}${view === "day" ? ` ${styles.viewBtnActive}` : ""}`}
            onClick={() => onViewChange("day")}
            aria-pressed={view === "day"}
          >
            Dia
          </button>
          <button
            className={`${styles.viewBtn}${view === "month" ? ` ${styles.viewBtnActive}` : ""}`}
            onClick={() => onViewChange("month")}
            aria-pressed={view === "month"}
          >
            Mês
          </button>
        </div>
      </div>

      {hasProfessionals && (
        <ul className={styles.legend} aria-label="Legenda de profissionais">
          {professionals.map((prof) => (
            <li key={prof.id} className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{
                  background:
                    professionalColors[prof.id] ?? getProfessionalColor(prof.id),
                }}
              />
              {prof.user?.name ?? "Sem profissional"}
            </li>
          ))}
        </ul>
      )}
    </header>
  );
};

export default CalendarHeader;

/**
 * Rótulo do intervalo exibido (apenas para acessibilidade/contexto visual).
 * @param {"week"|"day"|"month"} view
 * @param {Date} currentDate
 * @param {Date} weekStart
 * @param {Date} weekEnd
 * @returns {string}
 */
export function buildRangeLabel(view, currentDate, weekStart, weekEnd) {
  if (view === "day") {
    return format(currentDate, "EEEE, dd 'de' MMMM", { locale: ptBR });
  }
  if (view === "month") {
    return format(currentDate, "MMMM 'de' yyyy", { locale: ptBR });
  }
  const startStr = format(weekStart, "dd MMM", { locale: ptBR });
  const endStr = format(weekEnd, "dd MMM", { locale: ptBR });
  return `${startStr} – ${endStr}`;
}
